import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return "Invalid date";
  }
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'open':
    case 'new':
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    case 'in progress':
    case 'assigned':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case 'resolved':
    case 'closed':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'pending':
      return 'bg-orange-100 text-orange-800 border border-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
};

// Helper function to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case 'low':
      return 'bg-green-100 text-green-800 border border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
};

// Generate random OTP code
const generateRandomCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Notification component
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, x: 100 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className={`fixed top-4 right-4 px-6 py-4 rounded-xl shadow-lg z-50 flex items-center ${
        type === "success" ? "bg-green-500" : "bg-red-500"
      } text-white`}
    >
      <span className="mr-2 text-xl">
        {type === "success" ? "✅" : "❌"}
      </span>
      <span>{message}</span>
      <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">
        ✕
      </button>
    </motion.div>
  );
};

function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [employeeName, setEmployeeName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [otpData, setOtpData] = useState({});
  const [showOtpInput, setShowOtpInput] = useState({});
  const [notification, setNotification] = useState(null);
  const [expandedTickets, setExpandedTickets] = useState({});

  const [delayReasons, setDelayReasons] = useState({});


  useEffect(() => {
    const fetchAssignedTickets = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Access token not found.");

        const decodedToken = decodeToken(token);
        const empId = decodedToken?.id;
        if (!empId) throw new Error("Employee ID not found in token");
        setEmployeeId(empId);

        // Fetch employees to get employee names
        const empRes = await fetch(
          "https://customersupport-jhy9.onrender.com/api/employees",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!empRes.ok) throw new Error(`Employees failed: ${empRes.status}`);
        const empData = await empRes.json();
        setEmployees(empData);

        // Get current employee name
        const currentEmployee = empData.find(emp => emp._id === empId);
        if (currentEmployee) {
          setEmployeeName(currentEmployee.teamMemberName);
        }

        // Fetch all tickets
        const ticketRes = await fetch(
          "https://customersupport-jhy9.onrender.com/api/support-ticket",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!ticketRes.ok) throw new Error(`Tickets failed: ${ticketRes.status}`);
        const ticketData = await ticketRes.json();
        const allTickets = ticketData.tickets || ticketData || [];

        // Filter tickets assigned to current employee and add customer details
        const assignedTickets = allTickets
          .filter(ticket => ticket.assignedTo === empId)
          .map(ticket => {
            return {
              ...ticket,
              customerName: ticket.customerName || "Unknown Customer",
              customerEmail: ticket.customerEmail || "N/A",
              customerPhone: ticket.customerMobile || "N/A",
            };
          })
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setTickets(assignedTickets);
      } catch (err) {
        console.error(err);
        setError("Error fetching assigned tickets ❌");
        setNotification({ message: "Error fetching assigned tickets", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedTickets();
  }, []);

  // Filter tickets based on status and priority
  const filteredTickets = tickets.filter(ticket => {
    const statusMatch = !statusFilter || ticket.status?.toLowerCase().includes(statusFilter.toLowerCase());
    const priorityMatch = !priorityFilter || ticket.priority?.toLowerCase() === priorityFilter.toLowerCase();
    return statusMatch && priorityMatch;
  });


  const saveDelayReason = async (ticketId) => {
  try {
    const token = localStorage.getItem("authToken");
    const reason = delayReasons[ticketId] || "";

    if (!reason.trim()) {
      setNotification({ message: "Please enter a delay reason", type: "error" });
      return;
    }

    const res = await fetch(
      `https://customersupport-jhy9.onrender.com/api/support-ticket/${ticketId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delayReason: reason,
          updatedAt: new Date().toISOString(),
        }),
      }
    );

    if (!res.ok) throw new Error("Failed to save delay reason");

    setNotification({ message: "Delay reason saved successfully ✅", type: "success" });

    // Update UI locally
    setTickets(prev =>
      prev.map(ticket =>
        ticket._id === ticketId
          ? { ...ticket, delayReason: reason, updatedAt: new Date().toISOString() }
          : ticket
      )
    );
  } catch (err) {
    console.error("Error saving delay reason:", err);
    setNotification({ message: "Error saving delay reason ❌", type: "error" });
  }
};


  // Update ticket status
  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `https://customersupport-jhy9.onrender.com/api/support-ticket/${ticketId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            updatedAt: new Date().toISOString(),
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to update ticket status");

      // Update UI locally
      setTickets(prev =>
        prev.map(ticket =>
          ticket._id === ticketId
            ? { ...ticket, status: newStatus, updatedAt: new Date().toISOString() }
            : ticket
        )
      );

      return true;
    } catch (err) {
      console.error(err);
      setNotification({ message: "Error updating ticket status", type: "error" });
      return false;
    }
  };

  // Close ticket (after resolved)
  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to close this ticket?")) return;
    
    const success = await updateTicketStatus(ticketId, "closed");
    if (success) {
      setNotification({ message: "Ticket closed successfully", type: "success" });
    }
  };

  // Start working on ticket
  const handleStartTicket = async (ticketId) => {
    const success = await updateTicketStatus(ticketId, "in progress");
    if (success) {
      setNotification({ message: "Ticket status updated to 'In Progress'", type: "success" });
    }
  };

const handleSendOtp = async (ticketId, customerPhone) => {
  try {
    if (!customerPhone || customerPhone.trim().length !== 10) {
      setNotification({ message: "Invalid phone number. Please check the customer's phone number.", type: "error" });
      return;
    }

    const otp = generateRandomCode();
    console.log("Generated OTP:", otp, "for phone:", customerPhone, "ticket:", ticketId);

    // Save OTP state
    setOtpData(prev => ({
      ...prev,
      [ticketId]: { code: otp, sent: true, verified: false, timestamp: Date.now() }
    }));
    setShowOtpInput(prev => ({ ...prev, [ticketId]: true }));

    // SMS API params (unchanged)
    const username = "Experts";
    const authkey = "ba9dcdcdfcXX";
    const mobiles = "+91" + customerPhone.trim();
    const senderId = "EXTSKL";
    const accusage = "1";
    const baseUrl = "https://mobicomm.dove-sms.com/submitsms.jsp";

    const otpMessage = `Your Verification Code for login is ${otp} sent by megha. - Expertskill Technology.`;

    // Helper to send SMS (unchanged)
    const sendSms = async (message) => {
      const encodedMessage = encodeURIComponent(message);
      const url = `${baseUrl}?user=${username}&key=${authkey}&mobile=${mobiles}&message=${encodedMessage}&accusage=${accusage}&senderid=${senderId}`;
      try {
        await fetch(url, { mode: "no-cors" });
        return true;
      } catch (err) {
        console.error("SMS API error:", err);
        return false;
      }
    };

    // Send OTP via SMS (unchanged)
    const otpSent = await sendSms(otpMessage);
    if (otpSent) {
      setNotification({ message: "OTP sent successfully", type: "success" });
    } else {
      setNotification({ message: "Failed to send OTP", type: "error" });
      return;
    }

    // WhatsApp API call (improved)
    const WHATSAPP_API_BASE_URL = "https://webhook.whatapi.in/webhook/68cd2b2ebde42bbd90778727";
    const customerWhatsAppNumber = `91${customerPhone.trim()}`;
    const templateName = "support_review";
    const feedbackUrl = `https://digitalschool.cloud/feedback/${ticketId}`;
    
    // Template data with body_variables (if your template has text placeholders like {{1}}, {{2}})
    // Adjust variables based on your template structure (e.g., {{1}} = "Feedback Please", but body is fixed in template)
    const templateData = {


  button_variables: [ticketId] // Only ticketId, not the full URL
};

    console.log("📤 Sending WhatsApp template data:", JSON.stringify(templateData, null, 2));

    let whatsappSuccess = false;

    try {
      const response = await fetch(`${WHATSAPP_API_BASE_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(templateData)
      });

      const result = await response.json().catch(() => ({}));
      
      console.log("📥 WhatsApp response:", {
        status: response.status,
        statusText: response.statusText,
        data: result
      });

      if (response.ok && (result.message_status === "queued" || result.accepted || result.success)) {
        whatsappSuccess = true;
        console.log("✅ WhatsApp template sent successfully with expected message structure");
        setNotification({ message: "WhatsApp feedback message sent successfully (with 'Click Here' button)", type: "success" });
      } else {
        console.log("❌ WhatsApp template failed:", result.error || "Unknown error");
      }
    } catch (err) {
      console.error("❌ WhatsApp template API error:", err);
    }

    // Fallback 1: Custom WhatsApp message (non-template) if template fails
    if (!whatsappSuccess) {
      console.log("🔄 Trying custom WhatsApp message fallback...");
      const customMessage = `Feedback Please\nHow would you rate our service?\nThanks for your support\nClick Here: ${feedbackUrl}`;
      
      const customUrl = `${WHATSAPP_API_BASE_URL}?number=${customerWhatsAppNumber}&message=${encodeURIComponent(customMessage)}`;
      
      try {
        const customResponse = await fetch(customUrl, { method: "GET" });
        const customResult = await customResponse.json().catch(() => ({}));
        
        console.log("📥 Custom WhatsApp response:", {
          status: customResponse.status,
          data: customResult
        });

        if (customResponse.ok && customResult.accepted) {
          whatsappSuccess = true;
          console.log("✅ Custom WhatsApp message sent successfully");
          setNotification({ message: "Custom WhatsApp feedback message sent successfully", type: "success" });
        } else {
          console.log("❌ Custom WhatsApp failed");
        }
      } catch (customErr) {
        console.error("❌ Custom WhatsApp error:", customErr);
      }
    }

    // Fallback 2: SMS if both WhatsApp attempts fail
    if (!whatsappSuccess) {
      console.log("🔄 All WhatsApp attempts failed, using SMS fallback");
      const linkMessage = `Feedback Please. How would you rate our service? Thanks for your support. Click Here: ${feedbackUrl} Ref: ${ticketId.substr(-6)} - Expertskill`;
      const smsFallbackSent = await sendSms(linkMessage);
      if (smsFallbackSent) {
        setNotification({ message: "WhatsApp failed, but feedback link sent via SMS", type: "warning" });
      } else {
        setNotification({ message: "Failed to send feedback request", type: "error" });
      }
    }

  } catch (err) {
    console.error("Unexpected error in handleSendOtp:", err);
    setNotification({ message: "An unexpected error occurred while sending OTP", type: "error" });
  }
};


  // Verify OTP and resolve ticket
  const handleVerifyOtp = async (ticketId, enteredOtp) => {
    try {
      const otpInfo = otpData[ticketId];
      
      if (!otpInfo) {
        setNotification({ message: "OTP data not found. Please send OTP again.", type: "error" });
        return;
      }
      
      // Check if OTP has expired (15 minutes)
      const OTP_EXPIRY_TIME = 15 * 60 * 1000;
      if (Date.now() - otpInfo.timestamp > OTP_EXPIRY_TIME) {
        setNotification({ message: "OTP has expired. Please request a new OTP.", type: "error" });
        setShowOtpInput(prev => ({ ...prev, [ticketId]: false }));
        return;
      }
      
      // Validate OTP format
      if (!enteredOtp || enteredOtp.length !== 6 || !/^\d{6}$/.test(enteredOtp)) {
        setNotification({ message: "Please enter a valid 6-digit OTP.", type: "error" });
        return;
      }
      
      if (otpInfo.code === enteredOtp) {
        // OTP verified successfully
        setOtpData(prev => ({
          ...prev,
          [ticketId]: {
            ...prev[ticketId],
            verified: true,
            verifiedAt: Date.now()
          }
        }));

        // Update ticket status to resolved
        const success = await updateTicketStatus(ticketId, "resolved");
        if (success) {
          setNotification({ message: "OTP verified successfully! Ticket marked as resolved", type: "success" });
          
          // Hide OTP input
          setShowOtpInput(prev => ({
            ...prev,
            [ticketId]: false
          }));
        }
      } else {
        setNotification({ message: "Invalid OTP. Please check and try again.", type: "error" });
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setNotification({ message: "Error verifying OTP. Please try again.", type: "error" });
    }
  };

  // Toggle ticket expansion
  const toggleTicketExpansion = (ticketId) => {
    setExpandedTickets(prev => ({
      ...prev,
      [ticketId]: !prev[ticketId]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <motion.div
          className="relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
            aria-label="Loading spinner"
            role="status"
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          />
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <span className="text-blue-600 font-medium">Loading your tickets...</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4">
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="text-center"
          >
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Error</h2>
            <p className="text-gray-700">{error}</p>
            <motion.button
              onClick={() => window.location.reload()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Get unique statuses and priorities for filters
  const uniqueStatuses = [...new Set(tickets.map(t => t.status).filter(Boolean))];
  const uniquePriorities = [...new Set(tickets.map(t => t.priority).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">

    <h1 className="text-4xl font-extrabold text-gray-900 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            My Support Tickets
          </h1>
          
      <motion.div 
        className="max-w-7xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <motion.div 
          className="mb-8 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          
          <p className="text-gray-600">Welcome back, {employeeName}! Here are the tickets assigned to you.</p>
          <div className="mt-4 flex justify-center items-center gap-4 text-sm text-gray-500">
            <span>Total Assigned: <span className="font-semibold text-blue-600">{tickets.length}</span></span>
            <span>Filtered Results: <span className="font-semibold text-green-600">{filteredTickets.length}</span></span>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div 
          className="mb-8 bg-white rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Filter Tickets</h3>
          <div className="flex flex-wrap gap-4">
            <motion.div 
              className="flex-1 min-w-[200px]"
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-xl p-3 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </motion.div>

            <motion.div 
              className="flex-1 min-w-[200px]"
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-xl p-3 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">All Priorities</option>
                {uniquePriorities.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </motion.div>

            {(statusFilter || priorityFilter) && (
              <motion.div 
                className="flex items-end"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={() => {
                    setStatusFilter("");
                    setPriorityFilter("");
                  }}
                  className="px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
                >
                  Clear Filters
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Tickets Display */}
        {filteredTickets.length === 0 ? (
          <motion.div 
            className="bg-white rounded-2xl p-8 text-center shadow-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-lg mb-2">
              {tickets.length === 0 ? "🎉 No tickets assigned to you yet!" : "No tickets match your current filters"}
            </p>
            {tickets.length === 0 && (
              <p className="text-gray-400">Check back later for new assignments.</p>
            )}
          </motion.div>
        ) : (
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            <AnimatePresence>
              {filteredTickets.map((ticket, index) => (
                <motion.div
                  key={ticket._id}
                  layout
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ 
                    y: -5, 
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" 
                  }}
                  className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100"
                >
                  {/* Ticket Header */}
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleTicketExpansion(ticket._id)}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 truncate pr-2">
                        {ticket.subject}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <motion.span 
                          animate={{ rotate: expandedTickets[ticket._id] ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          ▼
                        </motion.span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                        Priority: {ticket.priority}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-2">
                      <strong>Customer:</strong> {ticket.customerName} | {ticket.customerPhone}
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      Created: {formatDate(ticket.createdAt)} | Updated: {formatDate(ticket.updatedAt)}
                    </div>
                  </div>

                  {/* Expanded Ticket Details */}
                  <AnimatePresence>
                    {expandedTickets[ticket._id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 border-t border-gray-100">
                          <h4 className="text-sm font-medium text-gray-700 mb-2 mt-4">Description</h4>
                          <p className="text-sm text-gray-600 mb-4">{ticket.description}</p>
                          
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Service</h4>
                              <p className="text-sm text-gray-600">{ticket.service || "N/A"}</p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-1">Material</h4>
                              <p className="text-sm text-gray-600">{ticket.material || "N/A"}</p>
                            </div>
                          </div>

                          {/* Ticket Images */}
                          {ticket.image && ticket.image.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Attached Images</h4>
                              <div className="flex flex-wrap gap-3">
                                {ticket.image.map((imgUrl, idx) => (
                                  <motion.a
                                    key={idx}
                                    href={imgUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="block"
                                  >
                                    <img
                                      src={imgUrl}
                                      alt={`Ticket Image ${idx + 1}`}
                                      className="h-24 w-24 object-cover rounded-lg border border-gray-200"
                                    />
                                  </motion.a>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Delay Reason Input (only for in progress tickets) */}
{ticket.status?.toLowerCase() === "in progress" && (
  <div className="mb-4">
    <h4 className="text-sm font-medium text-gray-700 mb-1">Delay Reason</h4>
    <textarea
      rows="2"
      className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500"
      value={delayReasons[ticket._id] ?? ticket.delayReason ?? ""}
      onChange={(e) =>
        setDelayReasons((prev) => ({ ...prev, [ticket._id]: e.target.value }))
      }
      placeholder="Enter reason for delay..."
    />
    <button
      onClick={() => saveDelayReason(ticket._id)}
      className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      Save Delay Reason
    </button>
  </div>
)}


                          {/* Ticket Actions */}
                          <div className="mt-6 space-y-3">
                            {ticket.status === 'open' && (
                              <motion.button
                                onClick={() => handleStartTicket(ticket._id)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="w-full py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 text-sm font-semibold shadow-md transition-all"
                              >
                                Start Working
                              </motion.button>
                            )}

                            {ticket.status === 'in progress' && (
                              <div className="space-y-3">
                                <div className="flex gap-3">
                                  <motion.button
                                    onClick={() => handleSendOtp(ticket._id, ticket.customerPhone)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex-1 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 text-sm font-semibold shadow-md transition-all"
                                  >
                                    Send OTP to Customer
                                  </motion.button>

                                  <motion.button
                                    onClick={() => handleCloseTicket(ticket._id)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="flex-1 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 text-sm font-semibold shadow-md transition-all"
                                  >
                                    Close Ticket
                                  </motion.button>
                                </div>

                                {/* OTP input appears only if Send OTP clicked */}
                                {showOtpInput[ticket._id] && (
                                  <motion.div 
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ duration: 0.3 }}
                                    className="p-4 bg-gray-50 rounded-xl"
                                  >
                                    <p className="text-sm text-gray-600 mb-2">Enter OTP sent to customer:</p>
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        placeholder="Enter OTP"
                                        className="flex-1 border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        onChange={(e) => {
                                          const enteredOtp = e.target.value;
                                          if (enteredOtp.length === 6) {
                                            handleVerifyOtp(ticket._id, enteredOtp);
                                          }
                                        }}
                                        maxLength={6}
                                      />
                                    </div>
                                  </motion.div>
                                )}
                              </div>
                            )}

                            {ticket.status === "closed" && (
                              <motion.button
                                onClick={() => handleSendOtp(ticket._id, ticket.customerPhone)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="w-full py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 text-sm font-semibold shadow-md transition-all"
                              >
                                Send OTP to Customer
                              </motion.button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default SupportTickets;